import React, { useState, useEffect, useCallback, memo } from 'react';
import {
  Box,
  Chip,
  Typography,
  FormHelperText,
  CircularProgress,
  TextField,
  Autocomplete
} from '@mui/material';
import { metaTagService } from '../../services/metaTagService';

const SHORT_HELPER = 'Search or Enter to add tag.';

const TagSelector = ({ 
  value = [], 
  onChange, 
  label = "Tags", 
  helperText,
  error = false,
  disabled = false,
  required = false,
  size = 'medium',
  onTagsCreated // Callback when new tags are created (to refresh tag list)
}) => {
  const displayHelper = helperText === '' ? undefined : (helperText || SHORT_HELPER);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  // Memoize the fetchTags function to prevent unnecessary re-renders
  const fetchTags = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage('');
      const response = await metaTagService.getActiveTags();
      
      if (Array.isArray(response)) {
        setTags(response);
      } else if (response && Array.isArray(response.results)) {
        setTags(response.results);
      } else {
        console.error('TagSelector: Unexpected response format:', response);
        setTags([]);
      }
    } catch (error) {
      console.error('TagSelector: Failed to fetch tags:', error);
      setErrorMessage('Failed to load tags');
    } finally {
      setLoading(false);
    }
  }, []);

  // Only fetch tags once when component mounts
  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  // Convert value to Autocomplete format
  // value can be: array of tag IDs (numbers) or array of mixed IDs and new tag names (strings/objects)
  const selectedTags = React.useMemo(() => {
    if (!Array.isArray(value) || value.length === 0) return [];
    
    return value.map(item => {
      // If it's a number, it's an existing tag ID
      if (typeof item === 'number') {
        const tag = tags.find(t => t.id === item);
        return tag || null;
      }
      // If it's a string, it's a new tag name
      if (typeof item === 'string') {
        return { id: null, name: item, _isNew: true, color: '#1976d2' };
      }
      // If it's an object with _isNew flag, it's a new tag
      if (item && typeof item === 'object' && item._isNew) {
        return item;
      }
      // If it's already a tag object
      if (item && typeof item === 'object' && item.id) {
        return item;
      }
      return null;
    }).filter(Boolean);
  }, [value, tags]);

  const handleChange = useCallback((event, newValue, reason, details) => {
    // newValue can contain:
    // - Existing tag objects (with id)
    // - New tag strings (from freeSolo input)
    // - New tag objects (with _isNew flag)
    
    const processedValue = newValue.map(item => {
      // If it's a string (from freeSolo), create a new tag object
      if (typeof item === 'string') {
        const trimmedName = item.trim();
        if (!trimmedName) return null;
        return { id: null, name: trimmedName, _isNew: true, color: '#1976d2' };
      }
      // If it's already an object
      if (item && typeof item === 'object') {
        // If it's a new tag, ensure it has the required fields
        if (item._isNew === true) {
          if (!item.name || typeof item.name !== 'string') {
            console.warn('Invalid new tag object:', item);
            return null;
          }
          return { id: null, name: item.name.trim(), _isNew: true, color: item.color || '#1976d2' };
        }
        // If it's an existing tag, ensure it has a valid ID
        if (item.id && typeof item.id === 'number' && item.id > 0) {
          return item;
        }
        // Invalid tag object
        console.warn('Invalid tag object (missing id):', item);
        return null;
      }
      return null;
    }).filter(Boolean);
    
    // Pass to parent: array containing tag IDs (numbers) and new tag objects
    const result = processedValue.map(item => {
      if (item._isNew === true) {
        // Return the new tag object so parent can create it
        return item;
      }
      // Return the tag ID for existing tags (must be a valid number)
      if (item.id && typeof item.id === 'number' && item.id > 0) {
        return item.id;
      }
      console.warn('Cannot extract valid ID from tag:', item);
      return null;
    }).filter(item => item !== null);
    
    onChange(result);
  }, [onChange]);

  // Handle creating new tag on Enter key or when freeSolo creates option
  const handleCreateOption = useCallback((inputValue) => {
    const newTagName = inputValue.trim();
    if (!newTagName) return null;
    
    // Check if tag already exists (case-insensitive)
    const existingTag = tags.find(t => t.name.toLowerCase() === newTagName.toLowerCase());
    if (existingTag) {
      return existingTag;
    }
    
    // Check if it's already in selected tags
    const alreadySelected = selectedTags.some(tag => 
      (tag._isNew && tag.name.toLowerCase() === newTagName.toLowerCase()) || 
      (!tag._isNew && tag.name.toLowerCase() === newTagName.toLowerCase())
    );
    if (alreadySelected) {
      return null;
    }
    
    // Create new tag object
    return { id: null, name: newTagName, _isNew: true, color: '#1976d2' };
  }, [tags, selectedTags]);

  // If there's an error loading tags, show error instead of hiding
  if (errorMessage) {
    return (
      <Box sx={{ p: 2, border: '1px solid #f44336', borderRadius: 1, bgcolor: '#ffebee' }}>
        <Typography variant="body2" color="error">
          Error loading tags: {errorMessage}
        </Typography>
        <Box 
          component="button"
          onClick={fetchTags} 
          sx={{ 
            mt: 1, 
            p: 1, 
            border: '1px solid #f44336', 
            borderRadius: 1, 
            background: 'transparent',
            cursor: 'pointer',
            color: '#f44336',
            '&:hover': { bgcolor: '#ffebee' }
          }}
        >
          Retry
        </Box>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary">
          Loading tags...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
    <Autocomplete
      multiple
      freeSolo
      options={tags}
      value={selectedTags}
      onChange={handleChange}
      sx={{ minWidth: 0 }}
      getOptionLabel={(option) => {
        if (typeof option === 'string') return option;
        return option.name || '';
      }}
      isOptionEqualToValue={(option, value) => {
        // Handle comparison for both existing tags and new tags
        if (typeof option === 'string' || typeof value === 'string') {
          return option === value;
        }
        if (option._isNew && value._isNew) {
          return option.name.toLowerCase() === value.name.toLowerCase();
        }
        if (option.id && value.id) {
          return option.id === value.id;
        }
        return false;
      }}
      filterOptions={(options, params) => {
        const inputValue = params.inputValue.trim();
        const filtered = options.filter(option =>
          option.name.toLowerCase().includes(params.inputValue.toLowerCase()) ||
          (option.description && option.description.toLowerCase().includes(params.inputValue.toLowerCase()))
        );
        const exactMatch = options.some(option =>
          option.name.toLowerCase() === inputValue.toLowerCase()
        );
        const alreadySelected = selectedTags.some(tag =>
          tag.name.toLowerCase() === inputValue.toLowerCase()
        );
        if (inputValue && !exactMatch && !alreadySelected) {
          filtered.push({
            id: null,
            name: inputValue,
            _isNew: true,
            color: '#1976d2',
          });
        }
        return filtered;
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          error={error}
          helperText={displayHelper}
          disabled={disabled}
          required={required}
          size={size}
          placeholder={!label ? SHORT_HELPER : undefined}
          FormHelperTextProps={{
            sx: {
              marginLeft: 0,
              marginRight: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
          }}
          sx={{ minWidth: 0 }}
        />
      )}
      renderTags={(value, getTagProps) => (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: size === 'small' ? 0.5 : 1 }}>
          {value.map((tag, index) => {
            const tagKey = tag._isNew ? `new-${tag.name}-${index}` : tag.id;
            return (
              <Chip
                {...getTagProps({ index })}
                key={tagKey}
                label={tag.name}
                size="small"
                sx={{
                  backgroundColor: tag.color || '#1976d2',
                  color: 'white',
                  border: tag._isNew ? '1px dashed rgba(255,255,255,0.5)' : 'none',
                  '& .MuiChip-deleteIcon': {
                    color: 'white',
                    '&:hover': {
                      color: 'rgba(255, 255, 255, 0.8)'
                    }
                  }
                }}
              />
            );
          })}
        </Box>
      )}
      renderOption={(props, option) => {
        // Handle both tag objects and strings (from freeSolo)
        if (typeof option === 'string' || option._isNew) {
          const labelText = typeof option === 'string' ? option : option.name;
          return (
            <Box component="li" {...props} key={`new-${labelText}`}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <Typography variant="body2" fontWeight="medium">
                  Create "{labelText}"
                </Typography>
              </Box>
            </Box>
          );
        }
        return (
          <Box component="li" {...props} key={option.id}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  backgroundColor: option.color || '#1976d2',
                  borderRadius: 1,
                  border: '1px solid #ddd',
                  flexShrink: 0
                }}
                aria-hidden="true"
              />
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight="medium" noWrap>
                  {option.name}
                </Typography>
                {option.description && (
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ width: '100%' }}>
                    {option.description}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        );
      }}
      noOptionsText="Press Enter to create a new tag"
      disabled={disabled}
    />
    </Box>
  );
};

TagSelector.displayName = 'TagSelector';

export default memo(TagSelector);
